/**
 * React Query bindings for the CRM mock API.
 *
 * Cache keys:
 *   - ['customers', 'summaries']
 *   - ['customers', 'profile', customerId]
 *
 * These are intentionally separate from the appointments feature cache —
 * CRM is a consumer of appointments, not a replacement. When an appointment
 * status changes elsewhere, components that want fresh timelines should
 * invalidate `['customers', 'profile']` (helper `invalidateCustomerCache`).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCustomer,
  createPet,
  findDuplicateCustomerByPhone,
  getCustomerProfile,
  listPets,
  listCustomerSummaries,
  updateCustomerNotes,
  type CreateCustomerPayload,
  type CreatePetPayload,
  type UpdateCustomerNotesPayload,
} from './customers-api';
import type { CustomerRecord, PetRecord } from '../appointments/types';

export const customerKeys = {
  all: ['customers'] as const,
  summaries: () => [...customerKeys.all, 'summaries'] as const,
  profile: (customerId: string) =>
    [...customerKeys.all, 'profile', customerId] as const,
  pets: (customerId?: string) => [...customerKeys.all, 'pets', customerId ?? 'all'] as const,
  duplicatePhone: (phone: string) => [...customerKeys.all, 'duplicate-phone', phone] as const,
};

export function useCustomerSummaries() {
  return useQuery({
    queryKey: customerKeys.summaries(),
    queryFn: listCustomerSummaries,
    staleTime: 60_000,
  });
}

export function useCustomerProfile(customerId: string | undefined) {
  return useQuery({
    queryKey: customerKeys.profile(customerId ?? '__none__'),
    queryFn: () => {
      if (!customerId) return Promise.resolve(null);
      return getCustomerProfile(customerId);
    },
    staleTime: 30_000,
    enabled: Boolean(customerId),
  });
}

export function useUpdateCustomerNotes() {
  const qc = useQueryClient();
  return useMutation<CustomerRecord, Error, UpdateCustomerNotesPayload>({
    mutationFn: updateCustomerNotes,
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: customerKeys.profile(variables.customerId) });
      qc.invalidateQueries({ queryKey: customerKeys.summaries() });
    },
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation<CustomerRecord, Error, CreateCustomerPayload>({
    mutationFn: createCustomer,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customerKeys.summaries() });
    },
  });
}

export function useCustomerDuplicateByPhone(phone: string | undefined) {
  const trimmedPhone = phone?.trim() ?? '';
  return useQuery({
    queryKey: customerKeys.duplicatePhone(trimmedPhone),
    queryFn: () => findDuplicateCustomerByPhone(trimmedPhone),
    staleTime: 15_000,
    enabled: trimmedPhone.replace(/\D/g, '').length >= 7,
  });
}

export function useCustomerPets(customerId?: string) {
  return useQuery({
    queryKey: customerKeys.pets(customerId),
    queryFn: () => listPets(customerId),
    staleTime: 30_000,
  });
}

export function useCreatePet() {
  const qc = useQueryClient();
  return useMutation<PetRecord, Error, CreatePetPayload>({
    mutationFn: createPet,
    onSuccess: (_pet, variables) => {
      qc.invalidateQueries({ queryKey: customerKeys.pets(variables.customerId) });
      qc.invalidateQueries({ queryKey: customerKeys.profile(variables.customerId) });
      qc.invalidateQueries({ queryKey: customerKeys.summaries() });
    },
  });
}
